import React from 'react'
import { cn } from '@/lib/utils'
import '../sections/control-targets3.css'

/**
 * OverridePin (W7, Control) — a small calendar-cell vignette pinned at an override
 * price. An override beats the engine and holds until the operator removes it.
 *
 * Copy is quoted verbatim from docs/marketing/2026-07-05-landing3-copy.md §W7.
 * £135 is illustrative fiction. The pin drops in ONCE on reveal via an
 * IntersectionObserver `is-live` gate; reduced motion resolves to the settled still
 * (handled in control-targets3.css).
 */
export default function OverridePin({ className }: { className?: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [live, setLive] = React.useState(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setLive(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setLive(true)
          io.disconnect()
        }
      },
      { threshold: 0.4 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      role="img"
      aria-label="A calendar cell pinned to an override price that holds until you remove it."
      className={cn('mkt3-panel flex flex-col items-center p-5 text-center sm:p-6', className)}
    >
      <span className="mkt3-label self-start">Override</span>

      {/* the pinned calendar cell */}
      <div className="relative mt-6 h-28 w-28">
        <div className="flex h-full w-full flex-col rounded-xl border-2 border-teal-500/70 bg-teal-50/60 p-2.5 shadow-[0_8px_24px_-12px_rgba(13,148,136,0.5)]">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-700">Sat 14</span>
          <span className="mt-auto text-2xl font-bold tabular-nums text-slate-900">£135</span>
          <span className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-teal-700">pinned</span>
        </div>

        {/* the pin itself, dropping into the corner */}
        <span
          className={cn(
            'mkt3-ctm-pin absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full bg-teal-600 text-white shadow-[0_4px_12px_-2px_rgba(13,148,136,0.7)]',
            live && 'is-live'
          )}
          aria-hidden="true"
        >
          {/* pin / thumbtack glyph */}
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M12 17v5" strokeLinecap="round" />
            <path
              d="M9 3h6l-1 6 3 3v2H7v-2l3-3-1-6Z"
              strokeLinejoin="round"
              fill="rgba(255,255,255,0.18)"
            />
          </svg>
        </span>
      </div>

      <p className="mt-5 text-sm text-slate-600">
        <span className="font-mono tabular-nums text-slate-700">£135 · pinned</span>
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-900">Yours until you remove it.</p>
    </div>
  )
}
